import React, { useState, useEffect } from 'react';
import { ShoppingItem, AlternativeProduct, User, ShoppingList, Invitation } from '../types';
import { generateAlternatives } from '../services/geminiService';
import { sendInvitation, getPendingInvitationsForList, removeUserFromList, cancelInvitation, checkUserStatus } from '../services/storageService';
import { ShareListWhatsAppModal } from './ShareListWhatsAppModal';

interface ShoppingListScreenProps {
  currentUser: User | null;
  activeList: ShoppingList;
  allLists: ShoppingList[];
  onSwitchList: (id: string) => void;
  onCreateList: (name: string) => void;
  onUpdateListName: (id: string, name: string) => void;
  onDeleteList: (id: string) => void;

  toggleItem: (id: string) => void;
  deleteItem: (id: string) => void;
  updateItemName: (id: string, name: string) => void;
  addItems: (items: {name: string, quantity?: string}[]) => void; // Changed to bulk add
  onCategorize?: () => void;
  
  isOnline: boolean;
  lastSynced: Date;
  onShowNotification: (msg: string) => void;
  onOpenSharedLink?: () => void;
}

const CATEGORY_ICONS: Record<string, string> = {
  "AGF (Groenten & Fruit)": "eco",
  "Zuivel, Eieren & Boter": "egg",
  "Bakkerij & Banket": "bakery_dining",
  "Vlees, Kip & Vis": "kebab_dining",
  "Voorraadkast & Kruiden": "inventory_2",
  "Dranken & Sappen": "local_bar",
  "Snacks & Snoep": "cookie",
  "Diepvries": "ac_unit",
  "Huishoudelijk & Verzorging": "cleaning_services",
  "Overig": "category"
};

export const ShoppingListScreen: React.FC<ShoppingListScreenProps> = ({ 
    currentUser,
    activeList,
    allLists,
    onSwitchList,
    onCreateList,
    onUpdateListName,
    onDeleteList,
    toggleItem, 
    deleteItem, 
    updateItemName, 
    addItems,
    onCategorize,
    isOnline,
    lastSynced,
    onShowNotification,
    onOpenSharedLink
}) => {
  const items = activeList.items || [];

  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [showAiModal, setShowAiModal] = useState(false);
  const [selectedItemForAi, setSelectedItemForAi] = useState<ShoppingItem | null>(null);
  const [alternatives, setAlternatives] = useState<AlternativeProduct[]>([]);
  const [loadingAi, setLoadingAi] = useState(false);
  const [isCategorizing, setIsCategorizing] = useState(false);
  const [isGrouped, setIsGrouped] = useState(false);
  
  const [showSidebar, setShowSidebar] = useState(false);
  const [showListSettingsModal, setShowListSettingsModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTopMenu, setShowTopMenu] = useState(false);

  const [shareEmail, setShareEmail] = useState("");
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsAppModalProps, setWhatsAppModalProps] = useState<{ targetEmail?: string; isUnknownUser?: boolean }>({});
  const [pendingInvites, setPendingInvites] = useState<Invitation[]>([]);

  const [isAdding, setIsAdding] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [editingListName, setEditingListName] = useState("");

  const toBuy = items.filter(i => !i.checked);
  const checked = items.filter(i => i.checked).sort((a, b) => (b.checkedAt || 0) - (a.checkedAt || 0));

  // Load pending invites and set name when settings modal opens
  useEffect(() => {
      if (showListSettingsModal) {
          setEditingListName(activeList.name); // Pre-fill name
          if(isOnline) {
             getPendingInvitationsForList(activeList.id).then(setPendingInvites);
          }
      }
  }, [showListSettingsModal, activeList.id, activeList.name, isOnline]);

  // Staat groeperen aan en komen er onbekende producten bij? Dan op de achtergrond indelen.
  const uncategorizedKey = toBuy.filter(i => !i.category).map(i => i.id).join(',');
  useEffect(() => {
      if (!isGrouped || !uncategorizedKey || isCategorizing || !onCategorize) return;
      const timer = setTimeout(() => {
          setIsCategorizing(true);
          Promise.resolve(onCategorize()).finally(() => setIsCategorizing(false));
      }, 800);
      return () => clearTimeout(timer);
  }, [isGrouped, uncategorizedKey]);

  // Group toBuy items by category
  const groupedToBuy: Record<string, ShoppingItem[]> = {};
  if (isGrouped) {
      toBuy.forEach(item => {
          const cat = item.category || "Overig";
          if (!groupedToBuy[cat]) groupedToBuy[cat] = [];
          groupedToBuy[cat].push(item);
      });
  }

  const categoryOrder = [
    "AGF (Groenten & Fruit)", "Zuivel, Eieren & Boter", "Bakkerij & Banket", "Vlees, Kip & Vis", 
    "Voorraadkast & Kruiden", "Dranken & Sappen", "Snacks & Snoep", "Diepvries", "Huishoudelijk & Verzorging", "Overig"
  ];

  const handleMenuClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setActiveMenuId(activeMenuId === id ? null : id);
  };

  const closeMenu = () => {
      setActiveMenuId(null);
      setShowTopMenu(false);
  };

  const handleAiClick = async (item: ShoppingItem) => {
    closeMenu();
    if (!currentUser) {
        onShowNotification("Log in om AI te gebruiken");
        return;
    }
    setSelectedItemForAi(item);
    setShowAiModal(true);
    setLoadingAi(true);
    setAlternatives([]);
    try {
        const results = await generateAlternatives(item.name);
        setAlternatives(results);
    } catch (e) {
        console.error(e);
    } finally {
        setLoadingAi(false);
    }
  };

  const applyAlternative = (altName: string) => {
    if (selectedItemForAi) {
        updateItemName(selectedItemForAi.id, altName);
        setShowAiModal(false);
    }
  };

  // Helper om input te parsen (bijv. "Bier 1 krat, Melk 2L")
  const parseAndAddItems = (rawInput: string) => {
      if (!rawInput.trim()) return;

      // 1. Splitsen op komma's
      const inputs = rawInput.split(',');
      const itemsToAdd: {name: string, quantity?: string}[] = [];

      inputs.forEach(input => {
          const cleaned = input.trim();
          if (!cleaned) return;

          // 2. Regex om te kijken of het eindigt op een hoeveelheid
          // Zoekt naar: Tekst (group 1) + Spatie + Cijfer[optioneel . , cijfers] + optioneel tekst (group 2)
          // Bijv: "Bier 1 krat", "Melk 2L", "Appels 1.5 kg"
          const quantityMatch = cleaned.match(/^(.*?)\s+(\d+(?:[.,]\d+)?\s*[a-zA-Z\s]*)$/);
          
          if (quantityMatch) {
              const name = quantityMatch[1].trim();
              const quantity = quantityMatch[2].trim();
              if (name && quantity) {
                  itemsToAdd.push({name, quantity});
                  return;
              }
          }
          
          // Fallback: geen hoeveelheid herkend
          itemsToAdd.push({name: cleaned});
      });

      if (itemsToAdd.length > 0) {
          addItems(itemsToAdd);
      }
  };

  const handleAddItem = () => {
    if (newItemName.trim()) {
        parseAndAddItems(newItemName);
        setNewItemName("");
    }
  };

  const startEditing = (item: ShoppingItem) => {
    setEditingId(item.id);
    setEditName(item.name);
    closeMenu();
  };

  const saveEdit = () => {
    if (editingId && editName.trim()) {
        updateItemName(editingId, editName.trim());
    }
    setEditingId(null);
    setEditName("");
  };

  const toggleGrouping = async () => {
      const nextGroupedState = !isGrouped;
      if (nextGroupedState) {
          // If turning on and items don't have categories, run AI
          const needsCategorization = toBuy.some(i => !i.category);
          if (needsCategorization) {
              if (onCategorize) {
                  setIsCategorizing(true);
                  try {
                      await onCategorize();
                  } finally {
                      setIsCategorizing(false);
                  }
              }
          }
      }
      setIsGrouped(nextGroupedState);
  };

  const handleCheckAndInvite = async () => {
      const email = shareEmail.trim().toLowerCase();
      if (!email) return;

      setIsCheckingUser(true);
      try {
          const status = await checkUserStatus(email, activeList);

          if (status.isAlreadyMember) {
              onShowNotification(`${email} heeft al toegang tot deze lijst.`);
              setIsCheckingUser(false);
              return;
          }

          if (status.exists) {
              // Known registered user
              if (currentUser && isOnline) {
                  setIsSendingInvite(true);
                  try {
                      await sendInvitation(currentUser, activeList.id, activeList.name, email);
                      onShowNotification(`Uitnodiging gestuurd naar ${status.name || email}!`);
                      setShareEmail("");
                      setShowShareModal(false);
                      getPendingInvitationsForList(activeList.id).then(setPendingInvites);
                  } catch (e) {
                      onShowNotification("Kon in-app uitnodiging niet versturen.");
                  } finally {
                      setIsSendingInvite(false);
                  }
              } else {
                  // User exists in registry, but active session is local/guest
                  setWhatsAppModalProps({ targetEmail: email, isUnknownUser: false });
                  setShowShareModal(false);
                  setShowWhatsAppModal(true);
              }
          } else {
              // Person is NOT in the app yet!
              // Requirement: "Iemand die nog niet in de app zit / als user bekend is moet gecheckt worden. Dan wil ik een modal die je eenvoudig naar het whatsapp account van deze gebruik kunt sturen zodat ze de app kunnen installeren en de gedeelde boodschappenlijst kunnen gebruiken."
              setWhatsAppModalProps({ targetEmail: email, isUnknownUser: true });
              setShowShareModal(false);
              setShowWhatsAppModal(true);
              onShowNotification(`${email} staat nog niet in de app. Stuur een uitnodiging via WhatsApp!`);
          }
      } catch (e) {
          console.error("Check user error:", e);
          setWhatsAppModalProps({ targetEmail: email, isUnknownUser: true });
          setShowShareModal(false);
          setShowWhatsAppModal(true);
      } finally {
          setIsCheckingUser(false);
      }
  };

  const handleOpenWhatsAppShare = () => {
      setWhatsAppModalProps({ isUnknownUser: false });
      setShowShareModal(false);
      setShowListSettingsModal(false);
      setShowWhatsAppModal(true);
  };

  const handleRemoveUser = async (userId: string) => {
      if(window.confirm("Wil je deze persoon de toegang ontzeggen?")) {
          await removeUserFromList(activeList.id, userId);
          onShowNotification("Toegang ingetrokken.");
      }
  };

  const handleCancelInvite = async (inviteId: string) => {
      if(window.confirm("Wil je deze uitnodiging intrekken?")) {
          await cancelInvitation(inviteId);
          setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
          onShowNotification("Uitnodiging ingetrokken.");
      }
  };

  const renderItem = (item: ShoppingItem) => (
    <div key={item.id} className="relative group">
        <div onClick={() => toggleItem(item.id)} className={`flex items-center gap-3 px-4 py-2 min-h-[48px] border-b border-gray-50 dark:border-white/5 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 transition-colors ${activeMenuId === item.id ? 'z-40 relative' : ''}`}>
            <div className="flex size-5 items-center justify-center shrink-0">
                <input type="checkbox" checked={item.checked} readOnly className="custom-checkbox h-5 w-5 rounded-full border-gray-300 dark:border-gray-500 border-2 bg-transparent text-primary focus:ring-0 cursor-pointer" />
            </div>
            <div className="flex flex-col flex-1 min-w-0 justify-center">
                {editingId === item.id ? (
                    <input autoFocus type="text" value={editName} onChange={(e) => setEditName(e.target.value)} onBlur={saveEdit} onKeyDown={(e) => e.key === 'Enter' && saveEdit()} onClick={(e) => e.stopPropagation()} className="bg-transparent border-none p-0 text-sm font-medium focus:ring-0 w-full" />
                ) : (
                    <div className="flex items-baseline gap-2 w-full overflow-hidden">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{item.name}</span>
                        {item.quantity && <span className="text-[11px] text-gray-400 font-medium truncate shrink-0">{item.quantity}</span>}
                    </div>
                )}
            </div>
            <button onClick={(e) => handleMenuClick(e, item.id)} className="flex items-center justify-center h-8 w-8 -mr-2 rounded-full text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10 transition shrink-0"><span className="material-symbols-outlined text-[18px]">more_vert</span></button>
        </div>
        {activeMenuId === item.id && (
            <>
            <div className="fixed inset-0 z-30 bg-black/20" onClick={closeMenu}></div>
            <div className="absolute right-4 top-10 z-50 w-full max-w-[280px] bg-white dark:bg-surface-dark rounded-xl shadow-2xl p-2 border border-gray-100 dark:border-white/10 origin-top-right">
                <button 
                    onClick={(e) => { e.stopPropagation(); handleAiClick(item); }} 
                    className={`flex items-center gap-3 w-full p-3 rounded-lg hover:bg-surface-light dark:hover:bg-white/5 text-left group ${!currentUser ? 'opacity-50' : ''}`}
                >
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-black transition-colors">
                        <span className="material-symbols-outlined text-[18px] filled">{currentUser ? 'auto_awesome' : 'lock'}</span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-sm font-bold">AI Varianten</span>
                        <span className="text-[10px] text-gray-500">{currentUser ? 'Andere merken of smaken' : 'Login vereist'}</span>
                    </div>
                </button>
                <div className="h-px w-full bg-gray-100 dark:bg-white/10 my-1"></div>
                <button onClick={(e) => { e.stopPropagation(); startEditing(item); }} className="flex items-center gap-3 w-full p-3 rounded-lg hover:bg-surface-light dark:hover:bg-white/5 text-left"><span className="material-symbols-outlined text-gray-500 text-[20px]">edit</span><span className="text-sm font-medium">Bewerk item</span></button>
                <button onClick={(e) => { e.stopPropagation(); deleteItem(item.id); closeMenu(); }} className="flex items-center gap-3 w-full p-3 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/10 text-left text-red-500"><span className="material-symbols-outlined text-[20px]">delete</span><span className="text-sm font-medium">Verwijder</span></button>
            </div>
            </>
        )}
    </div>
  );

  return (
    <div className="flex flex-col w-full h-screen bg-background-light dark:bg-background-dark" onClick={closeMenu}>
       {showSidebar && (
           <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm" onClick={() => { setShowSidebar(false); setIsCreatingList(false); }}></div>
       )}

       <aside className={`fixed top-0 left-0 h-full w-[80%] max-w-sm bg-surface-light dark:bg-surface-dark z-[70] shadow-2xl transform transition-transform duration-300 ease-out flex flex-col ${showSidebar ? 'translate-x-0' : '-translate-x-full'}`}>
            <div className="p-6 pt-12 border-b border-gray-100 dark:border-white/5">
                <h2 className="text-2xl font-bold mb-1">Mijn Lijsten</h2>
                <div className="flex items-center gap-2">
                    {isOnline ? (
                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 rounded-full">
                             <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                             <span className="text-[10px] font-bold text-green-700 dark:text-green-300">Cloud Synced</span>
                        </div>
                    ) : (
                        <span className="text-xs text-gray-500">Lokaal account</span>
                    )}
                </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {allLists.map(list => (
                    <button key={list.id} onClick={() => { onSwitchList(list.id); setShowSidebar(false); }} className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all ${activeList.id === list.id ? 'bg-primary text-black font-bold shadow-md' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5'}`}>
                        <span className="material-symbols-outlined text-[24px]">{activeList.id === list.id ? 'checklist' : 'list'}</span>
                        <div className="flex-1 text-left truncate">
                            <span className="block text-base truncate">{list.name}</span>
                            <span className="text-[10px] opacity-70 block">{list.items.filter(i => !i.checked).length} items</span>
                        </div>
                        {list.sharedWith && list.sharedWith.length > 0 && <span className="material-symbols-outlined text-[16px] opacity-50">group</span>}
                    </button>
                ))}
                {isCreatingList ? (
                     <form onSubmit={(e) => { e.preventDefault(); if(newListName.trim()){ onCreateList(newListName.trim()); setNewListName(""); setIsCreatingList(false); setShowSidebar(false); } }} className="mt-4 p-4 bg-white dark:bg-black/20 rounded-xl border border-gray-200 dark:border-white/10">
                         <input autoFocus type="text" className="w-full bg-transparent border-b border-gray-300 focus:border-primary px-0 py-1 font-bold" placeholder="Naam van lijst" value={newListName} onChange={(e) => setNewListName(e.target.value)} />
                         <div className="flex gap-2 justify-end mt-2"><button type="button" onClick={() => setIsCreatingList(false)} className="text-xs font-bold text-gray-400 p-2">Annuleren</button><button type="submit" className="text-xs font-bold text-primary p-2">Aanmaken</button></div>
                     </form>
                ) : (
                    <button onClick={() => setIsCreatingList(true)} className="w-full flex items-center gap-3 p-4 rounded-xl text-primary font-bold hover:bg-primary/5 transition-colors mt-2"><span className="material-symbols-outlined">add_circle</span><span>Nieuwe lijst</span></button>
                )}
            </div>
       </aside>

        <header className="flex-none z-20 bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-sm px-4 pt-6 pb-2 border-b border-gray-100 dark:border-white/5">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={() => setShowSidebar(true)} className="p-1 -ml-1 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition"><span className="material-symbols-outlined text-[28px]">menu</span></button>
                    <div>
                        <h1 onClick={() => setShowListSettingsModal(true)} className="tracking-tight text-xl font-bold leading-tight flex items-center gap-1 cursor-pointer active:opacity-70">{activeList.name}<span className="material-symbols-outlined text-[18px] text-gray-400">arrow_drop_down</span></h1>
                        {isOnline && <p className="text-[10px] text-gray-400 -mt-0.5">Update: {lastSynced.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</p>}
                    </div>
                </div>
                <div className="flex gap-1 relative">
                    <button onClick={() => setShowShareModal(true)} className="flex items-center justify-center w-10 h-10 rounded-full text-primary"><span className="material-symbols-outlined text-[22px] filled">person_add</span></button>
                    <button onClick={(e) => { e.stopPropagation(); setShowTopMenu(!showTopMenu); }} className="flex items-center justify-center w-10 h-10 rounded-full"><span className="material-symbols-outlined text-2xl">more_vert</span></button>
                    {showTopMenu && (
                         <div className="absolute right-0 top-12 z-50 w-48 bg-white dark:bg-surface-dark rounded-xl shadow-xl p-1 border border-gray-100 dark:border-white/10">
                             <button onClick={() => { setShowListSettingsModal(true); setShowTopMenu(false); }} className="flex items-center gap-3 w-full p-3 rounded-lg hover:bg-gray-50 text-sm font-medium"><span className="material-symbols-outlined text-[20px]">tune</span>Lijst instellingen</button>
                             {onOpenSharedLink && (
                                 <button onClick={() => { setShowTopMenu(false); onOpenSharedLink(); }} className="flex items-center gap-3 w-full p-3 rounded-lg hover:bg-gray-50 text-sm font-medium"><span className="material-symbols-outlined text-[20px]">content_paste</span>Gedeelde link openen</button>
                             )}
                             <button onClick={() => { onDeleteList(activeList.id); setShowTopMenu(false); }} className="flex items-center gap-3 w-full p-3 rounded-lg hover:bg-red-50 text-sm font-medium text-red-600"><span className="material-symbols-outlined text-[20px]">delete</span>Verwijder lijst</button>
                         </div>
                    )}
                </div>
            </div>
        </header>

      <div className="flex-1 overflow-y-auto no-scrollbar pb-32">
        <div className="px-4 pt-4 pb-2 flex items-center justify-between">
          <h3 className="text-xs font-bold uppercase text-gray-400 tracking-wider">Te kopen ({toBuy.length})</h3>
          {toBuy.length > 0 && (
              <button 
                onClick={toggleGrouping}
                disabled={isCategorizing}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full transition-all border ${isGrouped ? 'bg-primary border-primary text-black font-bold' : 'bg-transparent border-gray-200 dark:border-white/10 text-gray-500'}`}
              >
                  <span className={`material-symbols-outlined text-[16px] ${isCategorizing ? 'animate-spin' : ''}`}>
                      {isCategorizing ? 'sync' : (isGrouped ? 'splitscreen' : 'grid_view')}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider">{isCategorizing ? 'Sorteer...' : 'Groep'}</span>
              </button>
          )}
        </div>

        {isAdding && (
             <div className="flex items-center gap-3 bg-background-light dark:bg-background-dark px-4 py-2 min-h-[48px] border-b border-gray-50 dark:border-white/5">
                <div className="h-5 w-5 rounded-full border-2 border-gray-300 dark:border-gray-500"></div>
                <input 
                    autoFocus 
                    type="text" 
                    value={newItemName} 
                    onChange={(e) => setNewItemName(e.target.value)} 
                    onKeyDown={(e) => { 
                        if (e.key === 'Enter') { 
                            e.preventDefault(); 
                            handleAddItem(); 
                        } 
                        if (e.key === 'Escape') { 
                            setIsAdding(false); 
                            setNewItemName(""); 
                        } 
                    }} 
                    onBlur={() => { 
                        handleAddItem(); 
                        setIsAdding(false); 
                    }} 
                    placeholder="Wat wil je toevoegen?" 
                    className="flex-1 bg-transparent border-none focus:ring-0 p-0 text-sm font-medium placeholder-gray-400" 
                />
             </div>
        )}

        {isGrouped ? (
            categoryOrder.map(catName => {
                const itemsInCat = groupedToBuy[catName];
                if (!itemsInCat || itemsInCat.length === 0) return null;

                return (
                    <div key={catName} className="mb-0">
                        <div className="px-4 py-1 bg-gray-50/80 dark:bg-white/5 flex items-center gap-2 backdrop-blur-sm sticky top-0 z-10">
                            <span className="material-symbols-outlined text-[14px] text-primary">{CATEGORY_ICONS[catName] || 'category'}</span>
                            <span className="text-[10px] font-black uppercase tracking-widest text-gray-500">{catName}</span>
                        </div>
                        {itemsInCat.map(item => renderItem(item))}
                    </div>
                );
            })
        ) : (
            toBuy.map(item => renderItem(item))
        )}

        {checked.length > 0 && (
          <div className="flex flex-col mt-6">
            <div className="px-4 py-2 border-t border-gray-100 dark:border-white/5"><h3 className="text-xs font-bold uppercase text-gray-400 tracking-wider">Afgevinkt ({checked.length})</h3></div>
            <div className="opacity-60 grayscale-[0.5]">
            {checked.map(item => (
              <div key={item.id} onClick={() => toggleItem(item.id)} className="flex items-center gap-3 px-4 py-2 min-h-[48px] border-b border-gray-50 dark:border-white/5 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                  <div className="flex size-5 items-center justify-center shrink-0">
                    <input type="checkbox" checked={item.checked} readOnly className="custom-checkbox h-5 w-5 rounded-full border-primary border-2 bg-primary text-black focus:ring-0" />
                  </div>
                  <div className="flex-1 min-w-0">
                      <p className="text-gray-500 dark:text-gray-400 text-sm font-medium truncate line-through">{item.name}</p>
                  </div>
                  {item.quantity && <span className="text-[10px] text-gray-300 font-medium truncate shrink-0 ml-2 line-through">{item.quantity}</span>}
              </div>
            ))}
            </div>
          </div>
        )}
      </div>

      <button onClick={() => setIsAdding(true)} className="fixed bottom-24 right-4 z-40 h-14 w-14 rounded-full bg-accent-blue shadow-xl shadow-blue-500/40 flex items-center justify-center text-white"><span className="material-symbols-outlined text-3xl">add</span></button>

      {showShareModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowShareModal(false)}></div>
              <div className="bg-white dark:bg-[#1A2E22] w-full max-w-sm rounded-[28px] p-6 shadow-2xl relative z-10 border border-gray-100 dark:border-white/10 animate-in zoom-in-95 duration-200">
                  <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2.5">
                          <div className="w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold">
                              <span className="material-symbols-outlined text-2xl">group_add</span>
                          </div>
                          <div>
                              <h3 className="text-xl font-bold leading-tight">Lijst delen</h3>
                              <p className="text-xs text-gray-500 truncate max-w-[200px]">{activeList.name}</p>
                          </div>
                      </div>
                      <button onClick={() => setShowShareModal(false)} className="text-gray-400 hover:text-gray-600 p-1">
                          <span className="material-symbols-outlined text-xl">close</span>
                      </button>
                  </div>

                  <p className="text-xs text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                      Deel deze lijst direct via WhatsApp of nodig een bestaande gebruiker uit via e-mail.
                  </p>

                  {/* WhatsApp Quick Share Button */}
                  <button 
                      type="button"
                      onClick={handleOpenWhatsAppShare}
                      className="w-full py-3.5 px-4 rounded-2xl bg-[#25D366] hover:bg-[#22c35e] text-white font-bold text-sm shadow-lg shadow-green-500/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2.5 cursor-pointer mb-5"
                  >
                      <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.888 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      <span>Direct via WhatsApp delen</span>
                  </button>

                  <div className="relative flex py-2 items-center mb-4">
                      <div className="flex-grow border-t border-gray-200 dark:border-white/10"></div>
                      <span className="flex-shrink mx-3 text-[11px] font-bold text-gray-400 uppercase tracking-wider">of check via e-mail</span>
                      <div className="flex-grow border-t border-gray-200 dark:border-white/10"></div>
                  </div>

                  <form onSubmit={(e) => { e.preventDefault(); handleCheckAndInvite(); }} className="space-y-3">
                      <div>
                          <input 
                              type="email" 
                              required 
                              disabled={isCheckingUser || isSendingInvite} 
                              placeholder="naam@voorbeeld.nl" 
                              value={shareEmail} 
                              onChange={(e) => setShareEmail(e.target.value)} 
                              className="w-full bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-primary disabled:opacity-50" 
                          />
                          <p className="text-[11px] text-gray-400 mt-1.5 ml-1">
                              Is de ontvanger nog niet bekend? Dan opent direct de WhatsApp-uitnodiging.
                          </p>
                      </div>

                      <div className="flex gap-2 pt-1">
                          <button 
                              type="button" 
                              onClick={() => setShowShareModal(false)} 
                              className="flex-1 py-3 rounded-xl bg-gray-100 dark:bg-white/10 font-bold text-xs text-gray-700 dark:text-gray-300"
                          >
                              Annuleren
                          </button>
                          <button 
                              type="submit" 
                              disabled={isCheckingUser || isSendingInvite || !shareEmail.trim()} 
                              className="flex-1 py-3 rounded-xl bg-primary text-black font-bold text-xs flex items-center justify-center gap-1.5 disabled:opacity-50 cursor-pointer shadow-md shadow-primary/20"
                          >
                              {isCheckingUser ? (
                                  <>
                                      <div className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                                      <span>Checken...</span>
                                  </>
                              ) : isSendingInvite ? (
                                  <span>Versturen...</span>
                              ) : (
                                  <>
                                      <span className="material-symbols-outlined text-[16px]">person_search</span>
                                      <span>Check & Deel</span>
                                  </>
                              )}
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}

      {showListSettingsModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowListSettingsModal(false)}></div>
              <div className="bg-white dark:bg-[#1A2E22] w-full max-w-sm rounded-2xl p-6 shadow-2xl relative z-10 max-h-[85vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-6"><h3 className="text-xl font-bold">Lijst instellingen</h3><button onClick={() => setShowListSettingsModal(false)} className="text-gray-400"><span className="material-symbols-outlined">close</span></button></div>
                  <div className="space-y-6">
                      <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Lijstnaam</label><input type="text" value={editingListName} onChange={(e) => setEditingListName(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 outline-none font-bold" /></div>
                      
                      {/* Shared Users */}
                      <div>
                          <div className="flex items-center justify-between mb-2">
                              <label className="block text-xs font-bold text-gray-500 uppercase">Gedeeld met</label>
                              <button 
                                  type="button"
                                  onClick={handleOpenWhatsAppShare}
                                  className="flex items-center gap-1 text-[11px] font-bold text-[#25D366] hover:underline"
                              >
                                  <span className="material-symbols-outlined text-[16px]">share</span>
                                  <span>Deel via WhatsApp</span>
                              </button>
                          </div>
                          <div className="bg-gray-50 dark:bg-black/20 rounded-xl p-2 space-y-1">
                              <div className="flex items-center gap-3 p-2">
                                  <div className="size-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">{activeList.ownerName?.charAt(0) || currentUser?.name.charAt(0)}</div>
                                  <div className="flex-1"><p className="text-sm font-bold">{activeList.ownerName || currentUser?.name}</p><p className="text-[10px] text-gray-400">Eigenaar</p></div>
                              </div>
                              {activeList.sharedWith && activeList.sharedWith.map(u => (
                                  <div key={u.id} className="flex items-center justify-between p-2 border-t border-gray-100 dark:border-white/5">
                                      <div className="flex items-center gap-3">
                                        <div className="size-8 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">{u.photoUrl ? <img src={u.photoUrl} className="w-full h-full object-cover"/> : <span className="w-full h-full flex items-center justify-center text-[10px]">{u.name.charAt(0)}</span>}</div>
                                        <div><p className="text-sm font-bold">{u.name}</p><p className="text-[10px] text-gray-400">{u.email}</p></div>
                                      </div>
                                      {/* Only owner can remove people */}
                                      {(activeList.ownerId === currentUser?.id || !activeList.ownerId) && (
                                        <button onClick={() => handleRemoveUser(u.id)} className="p-2 text-red-500 bg-red-50 dark:bg-red-900/10 rounded-full hover:bg-red-100"><span className="material-symbols-outlined text-[18px]">person_remove</span></button>
                                      )}
                                  </div>
                              ))}
                              {(!activeList.sharedWith || activeList.sharedWith.length === 0) && <p className="text-xs text-gray-400 p-2 italic">Nog met niemand gedeeld.</p>}
                          </div>
                      </div>

                      {/* Pending Invites */}
                      {pendingInvites.length > 0 && (
                          <div>
                              <label className="block text-xs font-bold text-amber-500 uppercase mb-2">In afwachting</label>
                              <div className="bg-amber-50 dark:bg-amber-900/10 rounded-xl p-2 space-y-1 border border-amber-100 dark:border-amber-900/30">
                                  {pendingInvites.map(inv => (
                                      <div key={inv.id} className="flex items-center justify-between p-2">
                                          <div className="flex items-center gap-2">
                                              <span className="material-symbols-outlined text-amber-500 text-[20px]">mark_email_unread</span>
                                              <span className="text-sm text-gray-600 dark:text-gray-300">{inv.receiverEmail}</span>
                                          </div>
                                          <button onClick={() => handleCancelInvite(inv.id)} className="text-[10px] text-red-500 font-bold bg-white dark:bg-black/20 border border-red-100 px-2 py-0.5 rounded-full hover:bg-red-50">Intrekken</button>
                                      </div>
                                  ))}
                              </div>
                          </div>
                      )}

                      <div className="pt-2"><button onClick={() => { if(editingListName.trim() && editingListName !== activeList.name) onUpdateListName(activeList.id, editingListName.trim()); setShowListSettingsModal(false); }} className="w-full py-3 rounded-xl bg-primary text-black font-bold text-sm">Opslaan</button></div>
                  </div>
              </div>
          </div>
      )}

      {showAiModal && (
        <div className="fixed inset-0 z-[60]">
            <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setShowAiModal(false)}></div>
            <div className="fixed inset-x-0 bottom-0 z-10 w-full"><div className="flex min-h-full items-end justify-center"><div className="relative w-full rounded-t-[32px] bg-white dark:bg-[#1A2E22] p-6 pb-8 shadow-2xl max-h-[85vh] overflow-y-auto">
                <div className="flex justify-between items-start mb-6"><div><div className="flex items-center gap-2 mb-2"><span className="material-symbols-outlined text-primary text-lg filled">auto_awesome</span><span className="text-xs font-bold text-primary uppercase">AI Varianten</span></div><h3 className="text-2xl font-bold leading-tight">Opties voor<br/><span className="text-gray-400">{selectedItemForAi?.name}</span></h3></div><button onClick={() => setShowAiModal(false)} className="rounded-full p-2 bg-gray-50 text-gray-400"><span className="material-symbols-outlined">close</span></button></div>
                <div className="flex flex-col gap-3">{loadingAi ? <div className="flex flex-col items-center py-10 gap-4"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div><p className="text-sm text-gray-500">Opties zoeken...</p></div> : alternatives.map((alt, idx) => (
                    <div key={idx} className="flex flex-col p-4 bg-surface-light dark:bg-black/20 rounded-2xl border border-gray-100">
                        <div className="flex items-start justify-between gap-4 mb-2"><div className="flex flex-col gap-1"><span className="font-bold text-lg">{alt.name}</span><p className="text-sm text-gray-500 line-clamp-3">{alt.description}</p></div><button onClick={() => applyAlternative(alt.name)} className="shrink-0 px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg text-xs font-bold">{alt.action}</button></div>
                        <div className="flex flex-wrap gap-1.5 mt-2">{alt.tags.map((tag, tIdx) => <span key={tIdx} className={`inline-flex items-center px-2 py-1 rounded text-[10px] font-bold ${tag.colorClass}`}><span className="material-symbols-outlined mr-1" style={{ fontSize: '14px' }}>{tag.icon || 'label'}</span>{tag.label}</span>)}</div>
                    </div>
                ))}</div>
            </div></div></div>
        </div>
      )}

      {/* WhatsApp Share & Invite Modal */}
      <ShareListWhatsAppModal 
          isOpen={showWhatsAppModal}
          onClose={() => setShowWhatsAppModal(false)}
          list={activeList}
          currentUser={currentUser}
          targetEmail={whatsAppModalProps.targetEmail}
          isUnknownUser={whatsAppModalProps.isUnknownUser}
      />
    </div>
  );
};